import React, { useState } from 'react';
import {
  Drawer, Box, Typography, IconButton, Stack, Chip,
  Divider, Button, Skeleton,
} from '@mui/material';
import {
  Close, AssignmentReturn, Warning, CheckCircle, Print,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGetOrderReturnQuery } from '../../services/returnApi';
import { useToast } from '../common/ToastProvider';
import { usePrinterContext, browserPrintReturn } from '../../context/PrinterContext';
import { mapReturnToReceipt } from './receipt/mapReturnToReceipt';
import WhatsAppSection from './checkout/WhatsAppBillSection';
import { useGetReceiptTemplateQuery } from '../../services/receiptTemplateApi';

const REFUND_METHOD_LABELS = {
  cash: 'Cash',
  store_credit: 'Store Credit',
  same_payment_mode: 'Same Payment Mode',
};

const REFUND_METHOD_COLORS = {
  cash: { bgcolor: '#EFF6FF', color: '#2563EB' },
  store_credit: { bgcolor: '#F5F3FF', color: '#7C3AED' },
  same_payment_mode: { bgcolor: '#EEF2FF', color: '#4F46E5' },
};

function RefundMethodChip({ method }) {
  const cfg = REFUND_METHOD_COLORS[method] || { bgcolor: '#F1F5F9', color: '#64748B' };
  return (
    <Chip
      label={REFUND_METHOD_LABELS[method] || method}
      size="small"
      sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22, ...cfg }}
    />
  );
}

function ReturnStatusBadge({ status }) {
  const cfg = status === 'completed'
    ? { bgcolor: '#ECFDF5', color: '#059669', dot: '#10B981', label: 'Completed' }
    : { bgcolor: '#FFFBEB', color: '#B45309', dot: '#F59E0B', label: 'Draft' };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.35, borderRadius: '6px', bgcolor: cfg.bgcolor,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {status === 'completed' ? (
        <CheckCircle sx={{ fontSize: 12, color: cfg.dot }} />
      ) : (
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: cfg.dot }} />
      )}
      <Typography fontSize="0.7rem" fontWeight={700} color={cfg.color} lineHeight={1}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

const ReturnDetailDrawer = ({ open, onClose, orderReturn: summaryReturn, orderId }) => {
  const navigate = useNavigate();
  const showToast = useToast();
  const { isConnected, silentPrintReturn } = usePrinterContext();
  const { data: receiptTemplate } = useGetReceiptTemplateQuery();

  // Fetch full detail (with items, store, org) when drawer is open
  const resolvedOrderId = orderId || summaryReturn?.order_id;
  const { data: fullData, isFetching: isFetchingFull } = useGetOrderReturnQuery(
    { orderId: resolvedOrderId, id: summaryReturn?.id },
    { skip: !open || !resolvedOrderId || !summaryReturn?.id },
  );

  // Use full data when available, fall back to summary while loading
  const orderReturn = fullData?.data?.order_return || summaryReturn;
  const isLoading = isFetchingFull && !fullData;

const items = orderReturn?.items || [];
  // Cash/credit actually refunded (gross minus the points slice, which is
  // returned as points). Falls back to refund_amount for non-loyalty returns.
  const cashRefund = parseFloat(orderReturn?.monetary_refund_amount ?? orderReturn?.refund_amount ?? 0);
  const pointsRefunded = parseFloat(orderReturn?.loyalty_points_refunded ?? 0);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 420 },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#F8FAFC',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, bgcolor: '#fff', borderBottom: '1px solid #E2E8F0', flexShrink: 0 }}>
        {/* Row 1: icon + title + status + close */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            bgcolor: '#FFF7ED', display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0,
          }}>
            <AssignmentReturn sx={{ color: '#C2410C', fontSize: 18 }} />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography fontWeight={800} fontSize="0.92rem" color="#1E293B">
                {isLoading ? 'Loading…' : orderReturn?.return_number || 'Return Detail'}
              </Typography>
              {orderReturn && <ReturnStatusBadge status={orderReturn.status} />}
            </Box>
            {orderReturn && !isLoading && (
              <Box sx={{ mt: 0.25 }}>
                <Typography fontSize="0.68rem" color="#94A3B8">
                  {new Date(orderReturn.completed_at || orderReturn.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  {orderReturn.order_number && ` · Order ${orderReturn.order_number}`}
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: '#64748B', flexShrink: 0 }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* Row 2: action buttons */}
        {orderReturn?.status === 'completed' && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, ml: '52px' }}>
            <Button
              size="small"
              variant="outlined"
              disableElevation
              onClick={async () => {
                const receipt = mapReturnToReceipt(fullData || orderReturn, receiptTemplate);
                if (!receipt) return;
                if (isConnected) {
                  try { await silentPrintReturn(receipt); showToast('Return bill printed', 'success'); return; } catch (err) {
                    console.error('[Printer] silentPrintReturn failed:', err);
                    showToast('Printer error — switching to browser print', 'warning');
                  }
                } else {
                  showToast('Printer app not connected — printing via browser', 'info');
                }
                await new Promise((r) => setTimeout(r, 1500));
                browserPrintReturn(receipt);
              }}
              startIcon={<Print sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: '0.75rem',
                borderRadius: '8px', color: '#0369A1', borderColor: '#BAE6FD',
                '&:hover': { bgcolor: '#F0F9FF' },
              }}
            >
              Print
            </Button>
          </Box>
        )}

        {/* Send the credit note on WhatsApp -- its own 3-send limit, separate from the return screen's */}
        {orderReturn?.status === 'completed' && (
          <Box sx={{ mt: 1, mx: { xs: -1.5, sm: -2 }, ml: '36px' }}>
            <WhatsAppSection
              key={orderReturn.id}
              order={{ ...orderReturn, document_kind: 'credit_note' }}
              receipt={mapReturnToReceipt(fullData || orderReturn, receiptTemplate)}
              source="order_history"
              customerWhatsappOptIn={orderReturn.customer?.whatsapp_opt_in ?? null}
            />
          </Box>
        )}
      </Box>

      {/* Body */}
      <Box sx={{
        flexGrow: 1, overflowY: 'auto', p: 2,
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
      }}>
        {isLoading ? (
          <Stack spacing={1.5}>
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} height={56} sx={{ borderRadius: '10px' }} variant="rectangular" />
            ))}
          </Stack>
        ) : !orderReturn ? (
          <Box sx={{ textAlign: 'center', py: 6, color: '#94A3B8' }}>
            <AssignmentReturn sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
            <Typography fontSize="0.85rem" fontWeight={600}>Return not found</Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {/* Draft warning */}
            {orderReturn.status === 'draft' && (
              <Box sx={{
                bgcolor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px',
                p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1,
              }}>
                <Warning sx={{ fontSize: 15, color: '#F59E0B', flexShrink: 0, mt: 0.1 }} />
                <Typography fontSize="0.78rem" color="#92400E" fontWeight={600}>
                  Draft — items have not been refunded or restocked yet.
                </Typography>
              </Box>
            )}

            {/* Refund details */}
            <Box sx={{ bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', p: 2 }}>
              <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem" sx={{ mb: 1.25 }}>
                REFUND DETAILS
              </Typography>
              <Stack spacing={0.75}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography fontSize="0.78rem" color="#64748B">Refund Method</Typography>
                  <RefundMethodChip method={orderReturn.refund_method} />
                </Box>
                {orderReturn.credit_note_number && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography fontSize="0.78rem" color="#64748B">Credit Note</Typography>
                    <Typography fontSize="0.78rem" fontWeight={700} color="#1E293B">
                      {orderReturn.credit_note_number}
                    </Typography>
                  </Box>
                )}
                {pointsRefunded > 0 && orderReturn.status === 'completed' && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography fontSize="0.78rem" color="#64748B">Points Returned</Typography>
                    <Typography fontSize="0.82rem" fontWeight={700} color="#7E22CE">
                      {pointsRefunded.toFixed(0)} pts
                    </Typography>
                  </Box>
                )}
                {parseFloat(orderReturn.promotion_clawback_total ?? 0) > 0 && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography fontSize="0.78rem" color="#C2410C" sx={{ minWidth: 0 }}>
                        Promotion Reversed
                        {(orderReturn.promotion_clawback || [])[0]?.promotion_name
                          ? ` · ${(orderReturn.promotion_clawback || [])[0].promotion_name}`
                          : ''}
                      </Typography>
                      <Typography fontSize="0.82rem" fontWeight={700} color="#C2410C" sx={{ flexShrink: 0 }}>
                        −₹{parseFloat(orderReturn.promotion_clawback_total).toFixed(2)}
                      </Typography>
                    </Box>
                    {orderReturn.debit_note_number && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography fontSize="0.78rem" color="#64748B">Debit Note</Typography>
                        <Typography fontSize="0.78rem" fontWeight={700} color="#1E293B">
                          {orderReturn.debit_note_number}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography fontSize="0.88rem" fontWeight={800} color="#1E293B">
                    {pointsRefunded > 0 ? 'Cash Refunded' : 'Total Refunded'}
                  </Typography>
                  <Typography fontSize="1rem" fontWeight={900} color="#15803D">
                    {orderReturn.status === 'completed' ? `₹${cashRefund.toFixed(2)}` : '—'}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Items */}
            {isFetchingFull && items.length === 0 && (
              <Skeleton height={80} sx={{ borderRadius: '10px' }} variant="rectangular" />
            )}
            {items.length > 0 && (
              <Box>
                <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem" sx={{ mb: 1, px: 0.5 }}>
                  ITEMS RETURNED
                </Typography>
                <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                  {items.map((item, i) => (
                    <React.Fragment key={item.id || i}>
                      {i > 0 && <Divider />}
                      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: '8px', bgcolor: '#F1F5F9',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <AssignmentReturn sx={{ fontSize: 14, color: '#94A3B8' }} />
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography fontSize="0.82rem" fontWeight={700} color="#1E293B" noWrap>
                            {item.product_name}
                          </Typography>
                          <Typography fontSize="0.7rem" color="#64748B">
                            ×{parseFloat(item.quantity_returned)} returned
                            {item.reason_name && ` · Reason: ${item.reason_name}`}
                          </Typography>
                        </Box>
                        <Typography fontSize="0.85rem" fontWeight={700} color="#15803D" sx={{ flexShrink: 0 }}>
                          {item.refund_amount ? `₹${parseFloat(item.refund_amount).toFixed(2)}` : '—'}
                        </Typography>
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            )}

            {/* Notes */}
            {orderReturn.notes && (
              <Box sx={{ bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', px: 2, py: 1.25 }}>
                <Typography fontSize="0.68rem" fontWeight={800} color="#94A3B8" letterSpacing="0.06rem" sx={{ mb: 0.5 }}>
                  NOTES
                </Typography>
                <Typography fontSize="0.82rem" color="#374151" sx={{ whiteSpace: 'pre-wrap' }}>
                  {orderReturn.notes}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

export default ReturnDetailDrawer;
